/**
 * External app link tokens are bearer capabilities (`/share/apps/<token>`;
 * sharing review r6 #4). They must not reach analytics or logs, raw or
 * URL-encoded inside a return target (`returnTo=%2Fshare%2Fapps%2F…`, or
 * encoded twice through /auth/signing-in). Everything else is untouched.
 */

// A separator, raw or URL-encoded up to five times (%2F, %252F, %25252F, …).
const SEP = '(?:/|%(?:25){0,4}2F)';
const SHARE_TOKEN = new RegExp(
  `(${SEP}share${SEP}apps${SEP})[A-Za-z0-9_-]+`,
  'gi'
);
const PLACEHOLDER = '[token]';
const ENCODED_QUERY = '%(?:25){0,4}';
const PHONE_URL = new RegExp(
  `${SEP}connect${SEP}phone(?:\\?|${ENCODED_QUERY}3F)[^\\s"'<>#]*`,
  'gi'
);
const PHONE_CAPABILITY = new RegExp(
  `((?:[?&]|${ENCODED_QUERY}(?:3F|26))(?:token|purchase|resume)(?:=|${ENCODED_QUERY}3D))(?:(?!(?:[&#]|${ENCODED_QUERY}(?:26|23)))[^\\s"'<>])*`,
  'gi'
);

/** The text with every share link token replaced by `[token]`. */
export function redactShareTokens(text: string): string {
  const shared = /share/i.test(text)
    ? text.replace(SHARE_TOKEN, `$1${PLACEHOLDER}`)
    : text;
  return /connect/i.test(shared)
    ? shared.replace(PHONE_URL, (url) =>
        url.replace(PHONE_CAPABILITY, `$1${PLACEHOLDER}`)
      )
    : shared;
}

const MAX_DEPTH = 12;

function redactValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return redactShareTokens(value);
  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object')
    return value;
  if (Array.isArray(value))
    return value.map((item) => redactValue(item, depth + 1));
  // Keys too: heatmaps key their clicks by the page URL.
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value))
    out[redactShareTokens(key)] = redactValue(item, depth + 1);
  return out;
}

/**
 * Event or person properties with share tokens redacted in keys and values ($current_url,
 * $pathname, $referrer, $initial_*, $prev_pageview_*, any return target):
 * every string, a few levels deep.
 */
export function redactShareTokensInRecord(
  record: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record))
    out[redactShareTokens(key)] = redactValue(value, 1);
  return out;
}
